import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmModal } from '../../ConfirmModal';

type VocabItem = {
  id?: string;
  content: string;
  sounds_like: string[];
};

type ItemError = { content?: string; sounds_like?: string };

type Props = {
  value: VocabItem[];
  onChange: (value: VocabItem[] | undefined, error: string | null) => void;
  label?: string;
  required?: boolean;
  helperText?: string;
};

export const AdditionalVocabEditor: React.FC<Props> = ({
  value = [],
  onChange,
  label = 'Additional Vocabulary',
  required,
  helperText,
}) => {
  const [items, setItems] = useState<VocabItem[]>([]);
  const [itemErrors, setItemErrors] = useState<ItemError[]>([]);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [confirmDeleteItemIdx, setConfirmDeleteItemIdx] = useState<
    number | null
  >(null);
  const [confirmDeleteSound, setConfirmDeleteSound] = useState<{
    itemIdx: number;
    soundIdx: number;
  } | null>(null);

  const withIds = useMemo(() => {
    return value.map(item => ({ ...item, id: item.id ?? uuidv4() }));
  }, [value]);

  const validateItem = (item: VocabItem): ItemError => {
    const errors: ItemError = {};
    if (!item.content || item.content.trim() === '') {
      errors.content = 'Content is required';
    }
    if (
      !Array.isArray(item.sounds_like) ||
      item.sounds_like.length === 0 ||
      item.sounds_like.some(s => !s.trim())
    ) {
      errors.sounds_like = 'Sound is required';
    }
    return errors;
  };

  const validateAll = useCallback(
    (items: VocabItem[]) => {
      const errors = items.map(validateItem);
      const hasError = errors.some(e => e.content || e.sounds_like);
      setItemErrors(errors);
      onChange(
        items.map(({ id, ...rest }) => rest),
        hasError ? 'Some fields are invalid' : null
      );
    },
    [onChange]
  );

  useEffect(() => {
    setItems(withIds);
    setJsonText(JSON.stringify(value, null, 2));
    validateAll(withIds);
    setJsonError(null);

    setExpandedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateItem = (index: number, newItem: VocabItem) => {
    const updated = [...items];
    updated[index] = newItem;
    setItems(updated);
    setJsonText(
      JSON.stringify(
        updated.map(({ id, ...rest }) => rest),
        null,
        2
      )
    );
    validateAll(updated);
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);

    try {
      const parsed = JSON.parse(text);
      if (
        Array.isArray(parsed) &&
        parsed.every(
          item =>
            typeof item.content === 'string' &&
            Array.isArray(item.sounds_like) &&
            item.sounds_like.every((s: string) => typeof s === 'string')
        )
      ) {
        const withIds = parsed.map((item: VocabItem) => ({
          ...item,
          id: uuidv4(),
        }));
        setItems(withIds);
        validateAll(withIds);
        setJsonError(null);
      } else {
        setJsonError(
          'Invalid format: must be array with content and sounds_like'
        );
        onChange(undefined, 'Invalid format in JSON');
      }
    } catch {
      setJsonError('Invalid JSON');
      onChange(undefined, 'Invalid JSON');
    }
  };

  const addItem = () => {
    const newId = uuidv4();
    const newItem: VocabItem = { id: newId, content: '', sounds_like: [''] };
    const updated = [...items, newItem];
    setItems(updated);
    setJsonText(
      JSON.stringify(
        updated.map(({ id, ...rest }) => rest),
        null,
        2
      )
    );
    validateAll(updated);
    setExpandedIds([newId]);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    setJsonText(
      JSON.stringify(
        updated.map(({ id, ...rest }) => rest),
        null,
        2
      )
    );
    validateAll(updated);
  };

  const updateSound = (itemIdx: number, soundIdx: number, value: string) => {
    const updated = [...items];
    updated[itemIdx].sounds_like[soundIdx] = value;
    updateItem(itemIdx, updated[itemIdx]);
  };

  const addSound = (index: number) => {
    const updated = [...items];
    updated[index].sounds_like.push('');
    updateItem(index, updated[index]);
  };

  const removeSound = (itemIdx: number, soundIdx: number) => {
    const updated = [...items];
    updated[itemIdx].sounds_like.splice(soundIdx, 1);
    updateItem(itemIdx, updated[itemIdx]);
  };

  const handleExpandToggle = (id: string) => {
    setExpandedIds(prev =>
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
    );
  };

  const handleDeleteItem = (index: number) => {
    setConfirmDeleteItemIdx(index);
  };

  const handleConfirmDeleteItem = () => {
    if (confirmDeleteItemIdx !== null) {
      removeItem(confirmDeleteItemIdx);
      setConfirmDeleteItemIdx(null);
    }
  };

  const handleCancelDeleteItem = () => {
    setConfirmDeleteItemIdx(null);
  };

  const handleDeleteSound = (itemIdx: number, soundIdx: number) => {
    setConfirmDeleteSound({ itemIdx, soundIdx });
  };

  const handleConfirmDeleteSound = () => {
    if (confirmDeleteSound) {
      removeSound(confirmDeleteSound.itemIdx, confirmDeleteSound.soundIdx);
      setConfirmDeleteSound(null);
    }
  };

  const handleCancelDeleteSound = () => {
    setConfirmDeleteSound(null);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>

      <Stack spacing={2}>
        {items.map((item, i) => (
          <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={0.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <TextField
                  label="Content"
                  value={item.content}
                  onChange={e =>
                    updateItem(i, { ...item, content: e.target.value })
                  }
                  fullWidth
                  error={!!itemErrors[i]?.content}
                  helperText={itemErrors[i]?.content}
                  sx={{ flex: 1, mr: 1 }}
                />
                <IconButton onClick={() => handleExpandToggle(item.id!)}>
                  {expandedIds.includes(item.id!) ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
                <IconButton onClick={() => handleDeleteItem(i)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
              {expandedIds.includes(item.id!) && (
                <Stack spacing={0.5}>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>
                    Sounds Like
                  </Typography>
                  {item.sounds_like.map((sound, si) => (
                    <Box
                      key={si}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ mt: 0 }}
                    >
                      <TextField
                        value={sound}
                        onChange={e => updateSound(i, si, e.target.value)}
                        fullWidth
                        error={
                          !!itemErrors[i]?.sounds_like &&
                          (!sound.trim() || item.sounds_like.length === 0)
                        }
                        helperText={
                          !!itemErrors[i]?.sounds_like &&
                          (!sound.trim() || item.sounds_like.length === 0)
                            ? itemErrors[i]?.sounds_like
                            : undefined
                        }
                      />
                      <IconButton
                        onClick={() => handleDeleteSound(i, si)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    onClick={() => addSound(i)}
                    startIcon={<AddIcon />}
                    size="small"
                  >
                    <Typography variant="body1">Add Sound</Typography>
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Box mt={2}>
        <Button onClick={addItem} startIcon={<AddIcon />} variant="contained">
          Add Vocabulary
        </Button>
      </Box>

      <Box mt={4}>
        <TextField
          label="Vocabulary JSON"
          multiline
          rows={10}
          fullWidth
          value={jsonText}
          onChange={e => handleJsonChange(e.target.value)}
          error={!!jsonError}
          helperText={jsonError || helperText || 'Edit the raw JSON here.'}
          variant="outlined"
        />
      </Box>

      <ConfirmModal
        open={confirmDeleteItemIdx !== null}
        title="Delete Vocabulary Item?"
        onClose={handleCancelDeleteItem}
        onConfirm={handleConfirmDeleteItem}
        confirmButtonClassName="w-full bg-dangerSecondary hover:bg-danger border border-dangerSecondary text-contrast"
        confirmButtonLabel="Delete"
      >
        <div className="flex flex-col gap-y-md justify-center items-center mt-md">
          <p className="text-center text-3xl">
            Are you sure you want to delete this vocabulary item? This action
            cannot be undone.
          </p>
        </div>
      </ConfirmModal>
      <ConfirmModal
        open={!!confirmDeleteSound}
        title="Delete Sound?"
        onClose={handleCancelDeleteSound}
        onConfirm={handleConfirmDeleteSound}
      >
        <div className="flex flex-col gap-y-md justify-center items-center mt-md">
          <p className="text-center text-3xl">
            Are you sure you want to delete this sound? This action cannot be
            undone.
          </p>
        </div>
      </ConfirmModal>
    </Box>
  );
};
